import { useCallback, useMemo, useRef, useState } from "react";
import { useInfiniteQuery, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AroTypeFilter,
  getAroById,
  getChildAros,
  getRootAros,
  PyloAro,
  searchAros,
} from "@/services/acl.service";

interface GroupedSearchResults {
  parentId: string;
  parent: PyloAro | null;
  children: PyloAro[];
}

export const useRootAros = ({
  typeFilter,
  enabled = true,
  excludeEntityNames,
}: {
  typeFilter?: AroTypeFilter;
  enabled?: boolean;
  excludeEntityNames?: string[];
}) => {
  return useInfiniteQuery({
    queryKey: ["rootAros", typeFilter, excludeEntityNames],
    queryFn: ({ pageParam = 1 }) =>
      getRootAros({ page: pageParam, perPage: 50, typeFilter, excludeEntityNames }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more_pages ? lastPage.pagination.current_page + 1 : undefined,
    initialPageParam: 1,
    enabled,
  });
};

export const useChildAros = ({
  parentId,
  typeFilter,
  enabled = true,
  excludeEntityNames,
}: {
  parentId: string;
  typeFilter: AroTypeFilter;
  enabled?: boolean;
  excludeEntityNames?: string[];
}) => {
  return useInfiniteQuery({
    queryKey: ["childAros", parentId, typeFilter, excludeEntityNames],
    queryFn: ({ pageParam = 1 }) =>
      getChildAros({ parentId, page: pageParam, perPage: 50, typeFilter, excludeEntityNames }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more_pages ? lastPage.pagination.current_page + 1 : undefined,
    initialPageParam: 1,
    enabled: enabled && !!parentId,
  });
};

export const useSearchAros = ({
  searchTerm,
  typeFilter,
  enabled = true,
  excludeEntityNames,
}: {
  searchTerm: string;
  typeFilter: AroTypeFilter;
  enabled?: boolean;
  excludeEntityNames?: string[];
}) => {
  return useInfiniteQuery({
    queryKey: ["searchAros", searchTerm, typeFilter, excludeEntityNames],
    queryFn: ({ pageParam = 1, signal }) =>
      searchAros({
        searchTerm,
        page: pageParam,
        perPage: 50,
        typeFilter,
        excludeEntityNames,
        signal,
      }),
    getNextPageParam: (lastPage) =>
      lastPage.pagination.has_more_pages ? lastPage.pagination.current_page + 1 : undefined,
    initialPageParam: 1,
    enabled: enabled && searchTerm.length >= 2,
  });
};

export const useAroTree = () => {
  const queryClient = useQueryClient();
  const [aroSearch, setAroSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState<AroTypeFilter>("all");
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  const previousSearchRef = useRef<string>("");

  const isSearchMode = aroSearch.length >= 2;

  const rootArosQuery = useRootAros({
    typeFilter,
    enabled: !isSearchMode,
  });

  const searchQuery = useSearchAros({
    searchTerm: aroSearch,
    typeFilter,
    enabled: isSearchMode,
  });

  // Flatten root AROs from paginated data
  const rootAros = useMemo(() => {
    if (!rootArosQuery.data) return [];
    return rootArosQuery.data.pages.flatMap((page) => page.data);
  }, [rootArosQuery.data]);

  // Flatten search results from paginated data
  const searchResults = useMemo(() => {
    if (!searchQuery.data) return [];
    return searchQuery.data.pages.flatMap((page) => page.data);
  }, [searchQuery.data]);

  const groupedSearchResults = useMemo((): GroupedSearchResults[] => {
    if (!isSearchMode || searchResults.length === 0) return [];

    const grouped = new Map<string, PyloAro[]>();

    for (const aro of searchResults) {
      const parentId = aro.parent?.data?.id;
      if (parentId) {
        const existing = grouped.get(parentId) || [];
        existing.push(aro);
        grouped.set(parentId, existing);
      }
    }

    return Array.from(grouped.entries()).map(([parentId, children]) => ({
      parentId,
      parent: null, // Will be fetched separately
      children,
    }));
  }, [isSearchMode, searchResults]);

  // Extract unique parent IDs that need to be fetched
  const parentIdsToFetch = useMemo(() => {
    return groupedSearchResults.map((g) => g.parentId);
  }, [groupedSearchResults]);

  // Toggle expansion of an ARO node
  const toggleExpanded = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  // Expand a node (for auto-expansion in search mode)
  const expandNode = useCallback((id: string) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  // Collapse all nodes
  const collapseAll = useCallback(() => {
    setExpandedIds(new Set());
  }, []);

  // Reset search and collapse all when type filter changes
  const handleTypeFilterChange = useCallback((newFilter: AroTypeFilter) => {
    setTypeFilter(newFilter);
    setExpandedIds(new Set());
  }, []);

  // Reset expanded nodes when search term changes and cancel previous queries
  const handleSearchChange = useCallback(
    (newSearch: string) => {
      // Cancel previous search queries if the search term is changing
      if (previousSearchRef.current !== newSearch) {
        queryClient.cancelQueries({
          queryKey: ["searchAros", previousSearchRef.current],
        });
      }
      previousSearchRef.current = newSearch;

      setAroSearch(newSearch);
      if (newSearch.length < 2) {
        // When exiting search mode, collapse all
        setExpandedIds(new Set());
      }
    },
    [queryClient],
  );

  return {
    // State
    aroSearch,
    setAroSearch: handleSearchChange,
    typeFilter,
    setTypeFilter: handleTypeFilterChange,
    expandedIds,
    isSearchMode,

    // Data
    rootAros,
    searchResults,
    groupedSearchResults,
    parentIdsToFetch,

    // Actions
    toggleExpanded,
    expandNode,
    collapseAll,

    // Query states
    isLoadingRootAros: rootArosQuery.isLoading,
    isFetchingRootAros: rootArosQuery.isFetching,
    hasMoreRootAros: rootArosQuery.hasNextPage,
    fetchMoreRootAros: rootArosQuery.fetchNextPage,
    isFetchingMoreRootAros: rootArosQuery.isFetchingNextPage,

    isLoadingSearch: searchQuery.isLoading,
    isFetchingSearch: searchQuery.isFetching,
    hasMoreSearchResults: searchQuery.hasNextPage,
    fetchMoreSearchResults: searchQuery.fetchNextPage,
    isFetchingMoreSearchResults: searchQuery.isFetchingNextPage,
  };
};

// Hook for fetching children of a specific parent (used in lazy tree rendering)
export const useAroChildren = ({
  parentId,
  typeFilter,
  enabled,
  excludeEntityNames,
}: {
  parentId: string;
  typeFilter: AroTypeFilter;
  enabled: boolean;
  excludeEntityNames?: string[];
}) => {
  const childQuery = useChildAros({
    parentId,
    typeFilter,
    enabled,
    excludeEntityNames,
  });

  const children = useMemo(() => {
    if (!childQuery.data) return [];
    return childQuery.data.pages.flatMap((page) => page.data);
  }, [childQuery.data]);

  return {
    children,
    isLoading: childQuery.isLoading,
    isFetching: childQuery.isFetching,
    hasMore: childQuery.hasNextPage,
    fetchMore: childQuery.fetchNextPage,
    isFetchingMore: childQuery.isFetchingNextPage,
  };
};

// Hook for fetching a single parent ARO (used in search mode)
export const useParentAro = ({ parentId, enabled }: { parentId: string; enabled: boolean }) => {
  return useQuery({
    queryKey: ["aroById", parentId],
    queryFn: () => getAroById(parentId),
    enabled: enabled && !!parentId,
  });
};
